// src/pages/RefundPolicy.js
// Returns and refund policy — 14-day policy, EU cooling off period, and refund process.

import React from 'react';
import usePageTitle from '../hooks/usePageTitle';

const RefundPolicy = () => {
    usePageTitle('Return & Refund Policy', 'MAISON D\'OUD 14-day return policy, EU cooling off rights, and refund process.');

    return (
        <div className="bg-background min-h-screen pt-32 pb-24 px-6 transition-colors duration-300">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-4xl font-light text-foreground mb-2 tracking-wide">Return & Refund Policy</h1>
                <p className="text-muted-foreground text-sm mb-12">Last updated: April 23, 2026</p>

                <div className="space-y-10 text-muted-foreground leading-relaxed">

                    <section>
                        <p>We want you to be happy with your order. If something isn't right, we have a 14-day return policy meaning you have 14 days from the date you receive your item to request a return.</p>
                        <p className="mt-4">To be eligible, your item must be in the same condition as when you received it, unworn, unused, with tags attached and in its original packaging. You'll also need your receipt or proof of purchase.</p>
                        <p className="mt-4">To start a return, get in touch with us at <a href="mailto:support@maisondoud.com" className="text-[#CEAC8F] hover:underline">support@maisondoud.com</a>. We'll provide the return address once your request is accepted. Please note that items sent back without first contacting us will not be accepted.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">Trying Before You Commit</h2>
                        <p>Every order includes a free sample of each of our scents, so you can try the full range before deciding whether the one you ordered is right for you. If it isn't, you're welcome to return your bottle within the 14-day window — as long as it remains unopened, unused, and in its original packaging — and reorder your preferred scent instead.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">Damaged or Incorrect Items</h2>
                        <p>Please check your order as soon as it arrives. If your item is defective, damaged, or not what you ordered, contact us straight away at <a href="mailto:support@maisondoud.com" className="text-[#CEAC8F] hover:underline">support@maisondoud.com</a> and we'll do our best to make it right.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">Items We Can't Accept Back</h2>
                        <p>Once a bottle has been opened or used, it can't be returned for hygiene and safety reasons — this is what the complimentary samples are for, so you can try before opening your full-size bottle. Sale items and gift cards are also non-returnable by nature.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">Exchanges</h2>
                        <p>The simplest way to exchange an item is to return what you have and, once the return is accepted, place a new order for the item you'd like instead.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">European Union — 14 Day Cooling Off Period</h2>
                        <p>If your order is being shipped within the European Union, you have the right to cancel or return it within 14 days for any reason, no explanation needed. The item must still be in its original, unused condition with tags and packaging intact, and you'll need proof of purchase.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">Refunds</h2>
                        <p>Once we receive and inspect your return, we'll let you know whether it's been approved. If it is, your refund will be processed back to your original payment method within 10 business days. Keep in mind that your bank or card provider may take additional time to post the refund to your account.</p>
                        <p className="mt-4">If more than 15 business days have passed since we approved your return and you haven't received your refund, please contact us at <a href="mailto:support@maisondoud.com" className="text-[#CEAC8F] hover:underline">support@maisondoud.com</a>.</p>
                    </section>

                </div>
            </div>
        </div>
    );
};

export default RefundPolicy;